/**
 * PersonDisplay Component
 *
 * Reusable widget to display caller/callee (or any person) data from the backend.
 * Supports compact (avatar + name), inline (+ role), and full (+ mobile, nric) variants.
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';

import type { PersonDisplayData, PersonDisplayVariant } from './types';

export interface PersonDisplayProps {
  /** Person data (caller/callee object from API) */
  person: PersonDisplayData | null | undefined;
  /** Display density */
  variant?: PersonDisplayVariant;
  /** Size of avatar */
  avatarSize?: number;
  /** Fallback when person is null/undefined */
  fallbackLabel?: string;
}

function getInitials(name: string | null | undefined): string {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function PersonDisplay({
  person,
  variant = 'inline',
  avatarSize = 32,
  fallbackLabel = '—',
}: PersonDisplayProps) {
  if (person == null || (person.name == null && person.mobile_number == null)) {
    return (
      <Typography variant="body2" color="text.secondary">
        {fallbackLabel}
      </Typography>
    );
  }

  const displayName = person.name?.trim() || person.mobile_number || 'Unknown';
  const initials = getInitials(person.name);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 0,
      }}
    >
      <Avatar
        src={person.photo_url ?? undefined}
        sx={{
          width: avatarSize,
          height: avatarSize,
          fontSize: avatarSize * 0.4,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        {initials}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {displayName}
        </Typography>
        {variant !== 'compact' && (person.role ?? person.mobile_number ?? person.nric) && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {variant === 'full'
              ? [person.role, person.mobile_number, person.nric].filter(Boolean).join(' · ')
              : person.role ?? person.mobile_number ?? person.nric}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
